/**
 * Session Management Service
 * Handles single-device login enforcement and session tracking
 */

import { supabase } from '../lib/supabaseClient';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

interface ActiveSession {
  id: string;
  user_id: string;
  device_id: string;
  last_active_at: string;
  created_at: string;
  user_agent?: string;
  ip_address?: string;
}

class SessionManagementService {
  private deviceId: string | null = null;
  private fpPromise: Promise<any> | null = null;
  private sessionMonitorChannel: any = null;
  private isRegistering: boolean = false; // Flag to prevent logout during registration

  constructor() {
    // Initialize fingerprint library for device identification
    if (typeof window !== 'undefined') {
      this.fpPromise = FingerprintJS.load();
    }
  }

  /**
   * Setup real-time monitoring for session changes
   * If this device's session is deleted (e.g., by another device logging in), force logout
   */
  async setupSessionMonitoring(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('[SessionManagement] No session to monitor');
        return;
      }

      const deviceId = await this.getDeviceId();
      const userId = session.user.id;

      // Clean up existing channel if any
      if (this.sessionMonitorChannel) {
        await supabase.removeChannel(this.sessionMonitorChannel);
      }

      console.log('[SessionManagement] 👁️ Setting up real-time session monitoring for device:', deviceId);

      // Subscribe to changes in user_active_sessions table
      this.sessionMonitorChannel = supabase
        .channel(`session-monitor-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'user_active_sessions',
            filter: `user_id=eq.${userId}`
          },
          async (payload) => {
            console.log('[SessionManagement] 🚨 Session deletion detected:', payload);
            console.log('[SessionManagement] 🔍 Payload.old contents:', JSON.stringify(payload.old, null, 2));
            console.log('[SessionManagement] 🔍 Current device ID:', deviceId);
            console.log('[SessionManagement] 🔍 Deleted device ID:', payload.old?.device_id);
            
            // Skip if we're currently registering a session (prevents race condition)
            if (this.isRegistering) {
              console.log('[SessionManagement] ⏭️ Skipping DELETE event - session registration in progress');
              return;
            }
            
            // Check if the deleted session is for THIS device
            if (payload.old && payload.old.device_id === deviceId) {
              console.log('[SessionManagement] 🔴 THIS device session was deleted - forcing logout');
              
              // Force logout this device
              await supabase.auth.signOut();
              
              // Reload the page to show login screen
              window.location.reload();
            } else if (payload.old && !payload.old.device_id) {
              // FALLBACK: If device_id is not in payload (REPLICA IDENTITY not FULL),
              // we need to verify if OUR session still exists in the database
              console.warn('[SessionManagement] ⚠️ device_id missing from DELETE payload - checking if our session still exists');
              
              try {
                // Add small delay to avoid race conditions with session registration
                await new Promise(resolve => setTimeout(resolve, 500));
                
                const { data: ourSession, error: queryError } = await supabase
                  .from('user_active_sessions')
                  .select('*')
                  .eq('user_id', userId)
                  .eq('device_id', deviceId)
                  .single();
                
                // Handle 403 or other RLS errors gracefully
                if (queryError && queryError.code === 'PGRST301') {
                  console.warn('[SessionManagement] ⚠️ 403/RLS error during fallback check - assuming our session is valid');
                  return; // Don't log out on RLS errors
                }
                
                if (!ourSession) {
                  console.log('[SessionManagement] 🔴 Our session no longer exists in DB - forcing logout');
                  await supabase.auth.signOut();
                  window.location.reload();
                } else {
                  console.log('[SessionManagement] ✅ Our session still exists - another device was logged out');
                }
              } catch (fallbackError) {
                console.error('[SessionManagement] ⚠️ Fallback query error (non-fatal):', fallbackError);
                // Don't log out on query errors - assume session is valid
              }
            } else {
              console.log('[SessionManagement] ℹ️ Different device was logged out, not this one');
            }
          }
        )
        .subscribe((status) => {
          console.log('[SessionManagement] Real-time subscription status:', status);
        });
    } catch (error) {
      console.error('[SessionManagement] Error setting up session monitoring:', error);
    }
  }

  /**
   * Stop monitoring sessions (cleanup)
   */
  async stopSessionMonitoring(): Promise<void> {
    if (this.sessionMonitorChannel) {
      await supabase.removeChannel(this.sessionMonitorChannel);
      this.sessionMonitorChannel = null;
      console.log('[SessionManagement] Session monitoring stopped');
    }
  }

  /**
   * Get unique device identifier using browser fingerprinting
   */
  private async getDeviceId(): Promise<string> {
    if (this.deviceId) {
      return this.deviceId;
    }

    try {
      if (this.fpPromise) {
        const fp = await this.fpPromise;
        const result = await fp.get();
        this.deviceId = result.visitorId;
        return result.visitorId; // Return the string directly
      }
    } catch (error) {
      console.error('[SessionManagement] Failed to get device fingerprint:', error);
    }

    // Fallback: Generate a UUID and store in localStorage
    let storedDeviceId = localStorage.getItem('device_id');
    if (!storedDeviceId) {
      storedDeviceId = crypto.randomUUID();
      localStorage.setItem('device_id', storedDeviceId);
    }
    this.deviceId = storedDeviceId;
    return storedDeviceId; // Return the string directly instead of this.deviceId
  }

  // Token hashing removed - unnecessary complexity for device tracking
  // Session validation is handled by Supabase SDK and real-time monitoring

  /**
   * Start multi-device session flow (prevents race conditions)
   * Call this BEFORE deleting old device sessions
   */
  startMultiDeviceFlow(): void {
    console.log('[SessionManagement] 🔒 Starting multi-device flow - ignoring DELETE events');
    this.isRegistering = true;
  }

  /**
   * End multi-device session flow
   * Call this AFTER registration is complete
   */
  endMultiDeviceFlow(): void {
    console.log('[SessionManagement] 🔓 Ending multi-device flow - resuming normal operation');
    this.isRegistering = false;
  }

  /**
   * Register a new session for the current device
   * This will invalidate any existing session (single-device enforcement)
   */
  async registerSession(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('[SessionManagement] No session found');
        return;
      }

      const deviceId = await this.getDeviceId();
      const sessionToken = session.access_token;
      const userId = session.user.id;

      console.log('[SessionManagement] Registering session for device:', deviceId);

      // Check if there's already a session for this device
      const { data: existingSession } = await supabase
        .from('user_active_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('device_id', deviceId)
        .maybeSingle();

      if (existingSession) {
        console.log('[SessionManagement] Session already exists for this device, updating...');
        
        const { error: updateError } = await supabase
          .from('user_active_sessions')
          .update({
            last_active_at: new Date().toISOString(),
            session_token: sessionToken
          })
          .eq('id', existingSession.id);

        if (updateError) {
          console.error('[SessionManagement] Error updating session:', updateError);
        }
        return;
      }

      // Check for other device sessions (single-device enforcement)
      const { data: otherDeviceSessions } = await supabase
        .from('user_active_sessions')
        .select('*')
        .eq('user_id', userId)
        .neq('device_id', deviceId);

      if (otherDeviceSessions && otherDeviceSessions.length > 0) {
        console.log(`[SessionManagement] ⚠️ Found ${otherDeviceSessions.length} session(s) on other device(s) - removing them`);
        
        for (const sess of otherDeviceSessions) {
          await supabase
            .from('user_active_sessions')
            .delete()
            .eq('user_id', userId)
            .eq('device_id', sess.device_id);
          
          console.log(`[SessionManagement] 🔒 Logged out device: ${sess.device_id}`);
        }
      }
      
      // Insert new session
      const sessionData = {
        user_id: userId,
        device_id: deviceId,
        session_token: sessionToken,
        last_active_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('user_active_sessions')
        .upsert(sessionData, {
          onConflict: 'user_id,device_id'
        });

      if (error) {
        console.error('[SessionManagement] Error registering session:', error);
        return;
      }

      console.log('[SessionManagement] ✅ Session registered for this device:', deviceId);
    } catch (error) {
      console.error('[SessionManagement] Error in registerSession:', error);
    } finally {
      this.isRegistering = false;
    }
  }

  // validateSession() removed - unused method
  // Session validity is enforced by:
  // 1. Real-time monitoring (setupSessionMonitoring)
  // 2. Single-device enforcement in registerSession()
  // 3. Supabase SDK auto-refresh

  /**
   * Update last active timestamp for the current session
   */
  async updateLastActive(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase
        .from('user_active_sessions')
        .update({ last_active_at: new Date().toISOString() })
        .eq('user_id', session.user.id);
    } catch (error) {
      console.error('[SessionManagement] Error updating last active:', error);
    }
  }

  /**
   * Remove the current device's session
   */
  async removeSession(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase
        .from('user_active_sessions')
        .delete()
        .eq('user_id', session.user.id);
      
      console.log('[SessionManagement] Session removed');
    } catch (error) {
      console.error('[SessionManagement] Error removing session:', error);
    }
  }

  /**
   * Pre-create a session BEFORE login (used for multi-device flow)
   * This creates the session entry in the DB but doesn't affect auth state
   * Returns the user_id if successful
   */
  async preCreateSession(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[SessionManagement] Pre-creating session for user:', userId);
      
      const deviceId = await this.getDeviceId();

      const sessionData = {
        user_id: userId,
        device_id: deviceId,
        last_active_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
        ip_address: 'client'
      };

      const { error: insertError } = await supabase
        .from('user_active_sessions')
        .insert(sessionData);

      if (insertError) {
        console.error('[SessionManagement] Failed to pre-create session entry:', insertError);
        return { success: false, error: 'Failed to create session' };
      }

      console.log('[SessionManagement] ✅ Session pre-created for device:', deviceId);
      return { success: true };
    } catch (error) {
      console.error('[SessionManagement] Error pre-creating session:', error);
      return { success: false, error: 'Failed to pre-create session' };
    }
  }

  /**
   * Check if another device has an active session for a user (by email)
   * Used during login to detect multi-device conflicts
   * 
   * Uses RPC function to avoid RLS issues when querying by email
   */
  async checkExistingSession(email: string): Promise<{ hasExisting: boolean; deviceId?: string; userId?: string }> {
    try {
      console.log('[SessionManagement] Checking for existing sessions via RPC function');
      console.log('[SessionManagement] Looking up active sessions for email:', email);
      
      // Call RPC function that has SECURITY DEFINER to query auth.users and user_active_sessions
      const { data, error } = await supabase
        .rpc('check_existing_session_by_email', { user_email: email });

      if (error) {
        console.error('[SessionManagement] Error calling RPC function:', error);
        return { hasExisting: false };
      }

      console.log('[SessionManagement] RPC response:', data);

      // RPC returns an array with one row
      if (data && data.length > 0) {
        const result = data[0];
        
        if (result.has_existing) {
          console.log(`[SessionManagement] ⚠️ Found active session for user ${result.user_id} on device ${result.device_id}`);
          return { 
            hasExisting: true, 
            deviceId: result.device_id,
            userId: result.user_id
          };
        }
      }

      console.log('[SessionManagement] ✅ No existing sessions found for this user');
      return { hasExisting: false };
    } catch (error) {
      console.error('[SessionManagement] Error checking existing session:', error);
      return { hasExisting: false };
    }
  }

  /**
   * Cancel a pre-created session (delete current device's session)
   * Used when user cancels the multi-device modal
   */
  async cancelPreCreatedSession(): Promise<void> {
    try {
      const deviceId = await this.getDeviceId();
      console.log('[SessionManagement] Canceling pre-created session for device:', deviceId);

      // Delete the pre-created session entry by device_id
      const { error } = await supabase
        .from('user_active_sessions')
        .delete()
        .eq('device_id', deviceId);

      if (error) {
        console.error('[SessionManagement] Error deleting pre-created session:', error);
      } else {
        console.log('[SessionManagement] ✅ Pre-created session canceled');
      }
    } catch (error) {
      console.error('[SessionManagement] Error canceling pre-created session:', error);
    }
  }

  /**
   * Delete a specific session by device ID
   * Used to immediately log out another device before completing login
   */
  async deleteSessionByDeviceId(deviceId: string): Promise<void> {
    try {
      console.log('[SessionManagement] 🗑️ Deleting session for device:', deviceId);
      
      const { error } = await supabase
        .from('user_active_sessions')
        .delete()
        .eq('device_id', deviceId);

      if (error) {
        console.error('[SessionManagement] Error deleting session:', error);
      } else {
        console.log('[SessionManagement] ✅ Session deleted for device:', deviceId);
      }
    } catch (error) {
      console.error('[SessionManagement] Error deleting session:', error);
    }
  }

  /**
   * Force remove all sessions for a user (used when user chooses to continue on new device)
   */
  async forceRemoveAllSessions(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_active_sessions')
        .delete()
        .eq('user_id', userId);
      
      console.log('[SessionManagement] All sessions removed for user:', userId);
    } catch (error) {
      console.error('[SessionManagement] Error removing all sessions:', error);
    }
  }
}

export const sessionManagementService = new SessionManagementService();
